import { TPreviousDocumentData, EDocumentTypeMap, TFileUpload } from "@/types";
import { ClientDocument } from "@/proto/admin/all_pb";
import {
  ECountryCode,
  EDomicileTypes,
  EUserTypes,
} from "@/pagesCom/ClientInfo/index.dto";
import {
  ICommonFormData,
  ICompanyIDFormData,
  ICompanyPHFormData,
  IPersonalIDFormData,
  IPersonalPHFormData,
} from "@/pagesCom/ClientInfo/EditDocuments/index.dto";
import { uploadFile } from "../request-util";

interface IUploadedDocumentData {
  fileName: string;
  useCase: string;
  fileId: string;
}

interface IDocumentData {
  type: EDocumentTypeMap;
  files?: TFileUpload[];
  selfie?: TFileUpload[];
  uploadedFiles?: IUploadedDocumentData[];
  uploadedSelfies?: IUploadedDocumentData[];
}

export const formatPreviousDocuments = (
  payload: ClientDocument[]
): TPreviousDocumentData => {
  const output: TPreviousDocumentData = {} as TPreviousDocumentData;

  payload.forEach((item) => {
    const { filename, fileurl, usecase, id } = item.toObject();
    const [, fileType, fileIndex] = usecase.split("/");

    const payload = {
      fileId: id,
      fileName: filename,
      fileUrl: fileurl,
    };

    if (!output[fileType as EDocumentTypeMap])
      output[fileType as EDocumentTypeMap] = [];
    output[fileType as EDocumentTypeMap][Number(fileIndex)] = payload;
  });

  return output;
};

export const uploadSupportingDocuments = async (
  userType: EUserTypes,
  country: ECountryCode,
  domicile: EDomicileTypes,
  orgId: string,
  payload: any
): Promise<(string | undefined)[]> => {
  let documentsData: (IDocumentData | null)[] = [
    // Common documents for all flows
    {
      type: EDocumentTypeMap.SIGNED_MSA,
      files: (payload as ICommonFormData)[EDocumentTypeMap.SIGNED_MSA],
    },
  ];

  switch (country) {
    case ECountryCode.PH:
      if (userType === EUserTypes.INDIVIDUAL) {
        const data = payload as IPersonalPHFormData;
        documentsData = documentsData.concat([
          {
            type: data?.idType as EDocumentTypeMap,
            files: data?.documents,
            selfie: data?.documentSelfies,
          },
          {
            type: EDocumentTypeMap.SIGNATURE,
            files: data[EDocumentTypeMap.SIGNATURE],
          },
          {
            type: EDocumentTypeMap.DTI,
            files: data[EDocumentTypeMap.DTI],
          },
        ]);
      } else {
        const data = payload as ICompanyPHFormData;
        documentsData = documentsData
          .concat([
            domicile === EDomicileTypes.LOCAL
              ? {
                  type: EDocumentTypeMap.SEC_REGISTRATION,
                  files: data[EDocumentTypeMap.SEC_REGISTRATION],
                }
              : {
                  type: EDocumentTypeMap.ACRA,
                  files: data[EDocumentTypeMap.ACRA],
                },
            domicile === EDomicileTypes.LOCAL
              ? {
                  type: EDocumentTypeMap.ARTICLES_OF_INCORP,
                  files: data[EDocumentTypeMap.ARTICLES_OF_INCORP],
                }
              : null,
            domicile === EDomicileTypes.LOCAL
              ? {
                  type: EDocumentTypeMap.BENEFICIAL_OWNERSHIP,
                  files: data[EDocumentTypeMap.BENEFICIAL_OWNERSHIP],
                }
              : null,
            {
              type: EDocumentTypeMap.TIN,
              files: data[EDocumentTypeMap.TIN],
            },
          ])
          .filter(Boolean);
      }
      break;
    case ECountryCode.ID:
      if (userType === EUserTypes.INDIVIDUAL) {
        const data = payload as IPersonalIDFormData;
        documentsData = documentsData.concat([
          {
            type: data?.idType as EDocumentTypeMap,
            files: data?.documents,
            selfie: data?.documentSelfies,
          },
          {
            type: EDocumentTypeMap.SIGNATURE,
            files: data[EDocumentTypeMap.SIGNATURE],
          },
          domicile === EDomicileTypes.LOCAL
            ? {
                type: EDocumentTypeMap.NPWP,
                files: data[EDocumentTypeMap.NPWP],
              }
            : {
                type: EDocumentTypeMap.TIN,
                files: data[EDocumentTypeMap.TIN],
              },
        ]);
      } else {
        const data = payload as ICompanyIDFormData;
        documentsData = documentsData
          .concat([
            domicile === EDomicileTypes.LOCAL
              ? {
                  type: EDocumentTypeMap.DOE,
                  files: data[EDocumentTypeMap.DOE],
                }
              : {
                  type: EDocumentTypeMap.ACRA,
                  files: data[EDocumentTypeMap.ACRA],
                },
            domicile === EDomicileTypes.LOCAL
              ? {
                  type: EDocumentTypeMap.DOA,
                  files: data[EDocumentTypeMap.DOA],
                }
              : null,
            domicile === EDomicileTypes.LOCAL
              ? {
                  type: EDocumentTypeMap.LIST_OF_SHAREHOLDER,
                  files: data[EDocumentTypeMap.LIST_OF_SHAREHOLDER],
                }
              : null,
            domicile === EDomicileTypes.LOCAL
              ? {
                  type: EDocumentTypeMap.LIST_OF_DIRECTOR,
                  files: data[EDocumentTypeMap.LIST_OF_DIRECTOR],
                }
              : null,
            {
              type: EDocumentTypeMap.NIB_SKDP,
              files: data[EDocumentTypeMap.NIB_SKDP],
            },
            domicile === EDomicileTypes.LOCAL
              ? {
                  type: EDocumentTypeMap.SIUP,
                  files: data[EDocumentTypeMap.SIUP],
                }
              : {
                  type: EDocumentTypeMap.BUSINESS_PERMIT,
                  files: data[EDocumentTypeMap.BUSINESS_PERMIT],
                },
            domicile === EDomicileTypes.LOCAL
              ? {
                  type: EDocumentTypeMap.NPWP,
                  files: data[EDocumentTypeMap.NPWP],
                }
              : {
                  type: EDocumentTypeMap.TIN,
                  files: data[EDocumentTypeMap.TIN],
                },
          ])
          .filter(Boolean);
      }
      break;
    default:
      break;
  }

  /**
   * Use case format:
   * supporting-docs/[fileType]/[fileIndex (multiple files)]
   */
  const uploadPromises = documentsData.flatMap((data) => {
    const { type, files = [], selfie = [] } = data as IDocumentData;
    let uploadFiles: Promise<string | undefined>[] = [];
    const usecase = `upload-file/supporting-docs/${type}`;

    const handleUpload = (path: string, document: TFileUpload) =>
      !document.file || !document.file.size
        ? Promise.resolve<string | undefined>(document.id)
        : uploadFile({
            path,
            file: document.file as File,
            orgId,
          });

    if (files.length) {
      uploadFiles = uploadFiles.concat(
        files?.map((item, idx) => handleUpload(`${usecase}/${idx}`, item))
      );
    }
    if (selfie) {
      uploadFiles = uploadFiles.concat(
        selfie?.map((item, idx) =>
          handleUpload(`${usecase}__selfie/${idx}`, item)
        )
      );
    }
    return uploadFiles;
  });

  try {
    return await Promise.all(uploadPromises);
  } catch (err) {
    throw new Error(
      `Error with upload supporting docs: msg=${(err as any).message}`
    );
  }
};
